import styled, { keyframes } from "styled-components";
import { mediaMax } from "components/designV2/breakpoints";
import { Radius, Spacing } from "components/designV2/constants";
import { PageHeaderRow } from "components/designV2/layout/pageLayout";
import { Text } from "components/designV2/text/text";
import { ColorV2 } from "config/colors";
import { TypographyV2 } from "config/font";

const APPLICATION_GRID_COLUMNS = `
  minmax(160px, 1.5fr) repeat(5, minmax(110px, 1fr))
  minmax(140px, 1fr) 72px
`;

export const ListContainer = styled.div`
  margin-top: ${Spacing.md}px;
`;

export const ColumnListWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin: 0;
`;

export const EmptyState = styled(Text)`
  display: block;
  text-align: center;
  margin-top: 70px;
`;

export const ListWrapper = styled.div<{ $loading: boolean }>`
  opacity: ${(props) => (props.$loading ? "0.5" : "1.0")};
`;

export const ShiftRowCard = styled.div`
  background: ${ColorV2.White};
  border-radius: 20px;
  margin-top: 16px;
  padding: 12px 16px;
`;

export const ShiftSummary = styled.div`
  align-items: center;
  column-gap: 10px;
  cursor: pointer;
  display: grid;
  grid-template-columns: ${APPLICATION_GRID_COLUMNS};
  padding: 8px 0;

  &:focus-visible {
    outline: 2px solid ${ColorV2.Blue1};
    outline-offset: 2px;
  }
`;

export const ShiftTypeColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
`;

export const ShiftTypeLabel = styled.span`
  ${TypographyV2.s1};
  color: ${ColorV2.Blue4};
`;

export const ShiftTypeValue = styled.span`
  ${TypographyV2.b2};
  align-items: center;
  color: ${ColorV2.Blue1};
  display: inline-flex;
  gap: ${Spacing.xs}px;
  min-width: 0;
  white-space: nowrap;
`;

export const ShiftCompletion = styled.div`
  display: flex;
  justify-content: flex-start;
  min-width: 0;
`;

export const ShiftExpandButton = styled.button`
  align-items: center;
  background: transparent;
  border: none;
  cursor: pointer;
  display: inline-flex;
  height: 30px;
  justify-content: center;
  padding: 0;
  justify-self: end;
  width: 30px;
`;

export const ShiftApplicants = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 14px;
`;

export const ShiftApplicantsHeader = styled.div`
  align-items: center;
  display: flex;
  padding: 0 4px;
`;

export const ApplicantRow = styled.div`
  align-items: center;
  background: ${ColorV2.Blue7};
  border-radius: 16px;
  column-gap: 10px;
  cursor: pointer;
  display: grid;
  grid-template-columns: ${APPLICATION_GRID_COLUMNS};
  padding: 14px 0;
  position: relative;

  &:hover {
    background: ${ColorV2.Blue6};
  }

  &:focus-visible {
    outline: 2px solid ${ColorV2.Blue1};
    outline-offset: 2px;
  }
`;

export const ApplicantIdentity = styled.div<{ $hasSelection: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 4px;
  grid-column: 1 / 4;
  min-width: 0;
  padding-left: ${(props) => (props.$hasSelection ? "56px" : "0")};
`;

export const ApplicantRequestedRate = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  grid-column: 4;
`;

export const ApplicationSelection = styled.div`
  align-items: center;
  display: inline-flex;
  justify-content: center;
  left: 18px;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
`;

export const ApplicationActions = styled.div`
  align-items: center;
  display: inline-flex;
  gap: 12px;
  grid-column: 7 / -1;
  justify-content: flex-end;
  min-width: 0;
  padding-right: 48px;
`;

export const ApplicantExpandIcon = styled.span`
  align-items: center;
  display: inline-flex;
  position: absolute;
  right: 18px;
  top: 50%;
  transform: translateY(-50%);
`;

export const ApplicationStatusChipWrapper = styled.div`
  display: inline-flex;
  flex-shrink: 0;
`;

export const ApplicationPageHeader = styled(PageHeaderRow)`
  margin-bottom: ${Spacing.lg}px;
`;

export const ApplicationFilterControls = styled.div`
  align-items: flex-end;
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: ${Spacing.xl}px;
  margin-bottom: ${Spacing.lg}px;

  ${mediaMax.tablet} {
    align-items: flex-start;
    flex-direction: column;
    gap: ${Spacing.md}px;
  }
`;

export const SegmentOptionContent = styled.span`
  align-items: center;
  display: inline-flex;
  gap: ${Spacing.sm}px;
`;

export const PendingCountBadge = styled.span`
  ${TypographyV2.s1}
  align-items: center;
  background-color: ${ColorV2.NegativeRed};
  border-radius: 20px;
  box-sizing: border-box;
  color: ${ColorV2.White};
  display: inline-flex;
  height: 18px;
  justify-content: center;
  min-width: 18px;
  padding: 0 ${Spacing.xs}px;
`;

export const BulkActionBar = styled.div`
  bottom: ${Spacing.xl}px;
  display: flex;
  justify-content: center;
  left: 0;
  padding: 0 ${Spacing.xl}px;
  pointer-events: none;
  position: fixed;
  right: 0;
  z-index: 40;

  ${mediaMax.tablet} {
    bottom: ${Spacing.md}px;
    padding: 0 ${Spacing.md}px;
  }
`;

export const BulkActionBarCard = styled.div`
  align-items: center;
  background: ${ColorV2.White};
  border: 1px solid ${ColorV2.Blue6};
  border-radius: 44px;
  box-shadow: 0 18px 40px rgba(12, 45, 72, 0.16);
  display: flex;
  gap: ${Spacing.xl}px;
  justify-content: space-between;
  max-width: 1020px;
  padding: 28px 46px;
  pointer-events: auto;
  width: 100%;

  ${mediaMax.tablet} {
    gap: ${Spacing.md}px;
    padding: ${Spacing.lg}px ${Spacing.xl}px;
  }

  ${mediaMax.mobile} {
    align-items: stretch;
    border-radius: ${Radius.xl}px;
    flex-direction: column;
  }
`;

export const BulkActionBarCount = styled.span`
  ${TypographyV2.b1};
  color: ${ColorV2.Blue1};
`;

export const BulkActionBarButtons = styled.div`
  align-items: center;
  display: flex;
  gap: ${Spacing.lg}px;
  justify-content: flex-end;

  ${mediaMax.mobile} {
    justify-content: space-between;
  }
`;

export const ApplicationModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.lg}px;
`;

export const ApplicationModalActions = styled.div`
  align-items: center;
  display: flex;
  gap: ${Spacing.md}px;
  justify-content: flex-end;
  width: 100%;
`;

export const ApplicationLoading = styled.div`
  display: flex;
  justify-content: center;
  padding: ${Spacing.xl}px;
`;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

export const ApplicationSpinner = styled.span`
  animation: ${spin} 0.8s linear infinite;
  border: 3px solid ${ColorV2.Blue6};
  border-radius: 50%;
  border-top-color: ${ColorV2.Blue1};
  display: inline-block;
  height: 28px;
  width: 28px;
`;
